import {
  DXF_MAPPING_ROLE,
  type DxfEntityKey,
  type DxfMappingRole,
} from '@frontend/api';
import type { Selection, SelectionRef } from '@frontend/viewer-core';

import type { DrawingModel, RoleIndex } from './types';

/**
 * The bridge between the viewer's two vocabularies.
 *
 * Selection is keyed by loop and chunk — positional within one derivation, and
 * explicitly never persisted. Mapping is keyed by `DxfEntityKey`, which is stable
 * and is what the backend stores. Every mapping operation has to cross that
 * boundary, so it is crossed here and nowhere else: controllers speak entity keys,
 * the input layer speaks selection refs, and neither learns the other's shape.
 *
 * Pure functions over a model rather than closures over stores, for the same
 * reason `buildAccents` is: this is the computation, and giving it plain data in
 * and plain data out is what makes it testable without a session.
 */

/**
 * The entity keys a role assignment would land on.
 *
 * A loop ref resolves to every entity that contributed a chunk to it; an edge ref
 * resolves to the single entity behind that chunk. Selecting a whole loop is
 * therefore an assignment across *entities*, which is the granularity roles are
 * stored at — there is no such thing as mapping half of an entity.
 *
 * ⚠️ The reverse fan-out is the surprising one, and it is deliberate: because one
 * key can own chunks in several loops (see `chunksByEntityKey`), assigning a role
 * to a selected loop can recolour geometry the user did not select. That is
 * correct — the role belongs to the entity, not to the run of it that happens to
 * sit in this loop — but it means "what I selected" and "what changed" are not the
 * same set, and the popup's uniform-role check has to read from the entities, not
 * from the refs.
 */
export const selectionEntityKeys = (
  selection: Selection,
  model: DrawingModel,
): ReadonlySet<DxfEntityKey> => {
  const keys = new Set<DxfEntityKey>();

  selection.forEach((ref) => {
    const loop = model.loopsById.get(ref.loopId);
    if (!loop) {
      return;
    }
    if (ref.kind === 'edge') {
      const chunk = loop.chunks[ref.chunkIndex];
      if (chunk) {
        keys.add(chunk.entityId);
      }
      return;
    }
    loop.chunks.forEach((chunk) => keys.add(chunk.entityId));
  });

  return keys;
};

/**
 * Every chunk an entity key contributes, as its own continuous point list:
 * `[x0,y0,z0, x1,y1,z1, …]`, one array per chunk.
 *
 * A chunk is one entity's own contiguous contribution to one loop — a whole
 * circle, say — so its points are genuinely connected end to end and safe to
 * draw as a single joined polyline. Two different chunks are not: they can be
 * unrelated geometry on opposite sides of the drawing, so they stay separate
 * arrays here rather than being concatenated into one buffer. That separation
 * is what lets a dashed role draw each chunk as its own `Line2` with its own
 * distance-along-line, so a role's dash phase never leaks from one chunk's
 * geometry into an unrelated chunk's, and a closed loop's dash never inherits
 * whatever arbitrary phase preceded it in the batch.
 *
 * Hidden chunks are skipped, so painting a role can never reveal geometry the
 * user turned off. Keys with no stitched geometry contribute nothing — they
 * hold a role but have no line to color.
 */
export const entityKeyPolylines = (
  keys: Iterable<DxfEntityKey>,
  model: DrawingModel,
  hiddenLayers?: ReadonlySet<number>,
): readonly (readonly number[])[] => {
  const polylines: number[][] = [];

  for (const key of keys) {
    const addresses = model.chunksByEntityKey.get(key);
    if (!addresses) {
      continue;
    }
    for (const { loopId, chunkIndex } of addresses) {
      const chunk = model.loopsById.get(loopId)?.chunks[chunkIndex];
      if (!chunk || hiddenLayers?.has(chunk.layer)) {
        continue;
      }
      const { points } = chunk;
      if (points.length < 2) {
        continue;
      }
      const polyline: number[] = [];
      points.forEach(([x, y]) => polyline.push(x, y, 0));
      polylines.push(polyline);
    }
  }

  return polylines;
};

/** Whether an entity has at least one stitched chunk that is not on a hidden layer. */
export const entityKeyHasVisibleChunk = (
  key: DxfEntityKey,
  model: DrawingModel,
  hiddenLayers?: ReadonlySet<number>,
): boolean => {
  const addresses = model.chunksByEntityKey.get(key);
  if (!addresses) {
    return false;
  }
  return addresses.some(({ loopId, chunkIndex }) => {
    const chunk = model.loopsById.get(loopId)?.chunks[chunkIndex];
    return chunk !== undefined && !hiddenLayers?.has(chunk.layer);
  });
};

/**
 * Selection refs for the visible chunks of these entities.
 *
 * Skips hidden layers so a role action never grabs geometry the user turned off.
 * Uses edge refs rather than loop refs so a loop stitched across layers does not
 * pull in the hidden half.
 */
export const entityKeyVisibleSelectionRefs = (
  keys: Iterable<DxfEntityKey>,
  model: DrawingModel,
  hiddenLayers?: ReadonlySet<number>,
): SelectionRef[] => {
  const refs: SelectionRef[] = [];
  for (const key of keys) {
    const addresses = model.chunksByEntityKey.get(key);
    if (!addresses) {
      continue;
    }
    for (const { loopId, chunkIndex } of addresses) {
      const chunk = model.loopsById.get(loopId)?.chunks[chunkIndex];
      if (!chunk || hiddenLayers?.has(chunk.layer)) {
        continue;
      }
      refs.push({ kind: 'edge', loopId, chunkIndex });
    }
  }
  return refs;
};

/**
 * Every polyline's segment endpoint pairs, concatenated into one flat buffer
 * for a batch drawn as disconnected segments rather than joined `Line2`s.
 *
 * Only ever correct for a *solid* stroke: flattening erases the boundary
 * between polylines, so a dashed batch must draw from the polylines
 * themselves — see `entityKeyPolylines`'s own doc comment for why.
 */
export const flattenPolylinesToSegments = (
  polylines: readonly (readonly number[])[],
): number[] => {
  const positions: number[] = [];
  polylines.forEach((points) => {
    for (let index = 3; index < points.length; index += 3) {
      positions.push(
        points[index - 3],
        points[index - 2],
        points[index - 1],
        points[index],
        points[index + 1],
        points[index + 2],
      );
    }
  });
  return positions;
};

/**
 * Flattens entities into segment endpoint pairs for a solid stroke batch.
 *
 * `entityKeyPolylines` followed by `flattenPolylinesToSegments` — the shape a
 * dashed role needs is the polylines themselves; this is for every other
 * caller (accents, and the solid Cut role), which only ever draw disconnected
 * segments and have no reason to keep chunk boundaries around.
 */
export const entityKeyPositions = (
  keys: Iterable<DxfEntityKey>,
  model: DrawingModel,
  hiddenLayers?: ReadonlySet<number>,
): number[] =>
  flattenPolylinesToSegments(entityKeyPolylines(keys, model, hiddenLayers));

/**
 * Which roles can be selected by name, and which are wholly selected already.
 *
 * Both answers in one pass, because they walk the same data and the panel needs
 * them together — a row is inert, selectable, or selected.
 *
 * Counted over *selectable* entities only — stitched geometry on a layer the
 * user has hidden does not count, matching the role overlay and `selectRole`.
 * A role can hold geometry that can never
 * be picked: `ignore` is seeded with all the text and hatches, so measuring against
 * its raw entity count would leave it permanently un-selectable and never able to
 * read as fully selected. The same trap the layers panel has with
 * `entityCountsByLayer` — see `useDxfLayerPanel`.
 */
export const roleSelectionState = (
  selection: Selection,
  roleIndex: RoleIndex,
  model: DrawingModel,
  hiddenLayers?: ReadonlySet<number>,
): {
  selectableRoles: ReadonlySet<DxfMappingRole>;
  selectedRoles: ReadonlySet<DxfMappingRole>;
} => {
  const selected = selectionEntityKeys(selection, model);
  const selectableRoles = new Set<DxfMappingRole>();
  const selectedRoles = new Set<DxfMappingRole>();

  roleIndex.forEach((keys, role) => {
    let selectableCount = 0;
    let selectedCount = 0;
    keys.forEach((key) => {
      if (!entityKeyHasVisibleChunk(key, model, hiddenLayers)) {
        return;
      }
      selectableCount += 1;
      if (selected.has(key)) {
        selectedCount += 1;
      }
    });

    if (selectableCount === 0) {
      return;
    }
    selectableRoles.add(role);
    // All or nothing, as the panel needs it: a partially-caught role is not
    // selected, so the row only lights up once the selection covers the whole of it.
    if (selectedCount === selectableCount) {
      selectedRoles.add(role);
    }
  });

  return { selectableRoles, selectedRoles };
};

/**
 * Wire format to working copy.
 *
 * The server speaks a `Record` because that is what JSON has; the session works in
 * a `Map` because it is edited on every click over a drawing that may carry tens of
 * thousands of entities. The conversion happens here, at the boundary, and nowhere
 * else.
 */
export const toAssignmentsMap = (
  assignments: Readonly<Record<string, readonly DxfMappingRole[]>>,
): ReadonlyMap<DxfEntityKey, readonly DxfMappingRole[]> =>
  new Map(Object.entries(assignments));

/** Working copy to wire format. */
export const toAssignmentsPayload = (
  assignments: ReadonlyMap<DxfEntityKey, readonly DxfMappingRole[]>,
): Record<string, readonly string[]> => {
  const payload: Record<string, readonly string[]> = {};
  assignments.forEach((roles, key) => {
    // An entity with no roles is not "unmapped" to the backend — it fails the
    // write validator, which requires at least one. Dropping it is the honest
    // conversion: the key simply is not in the mapping.
    if (roles.length > 0) {
      payload[key] = roles;
    }
  });
  return payload;
};

/**
 * The layers the given entities draw on.
 *
 * Keys with no stitched geometry contribute nothing — they belong to a layer, but
 * they have no chunk to locate them by, and nothing about them is visible for a
 * layer toggle to affect.
 */
export const entityKeyLayers = (
  keys: Iterable<DxfEntityKey>,
  model: DrawingModel,
): ReadonlySet<number> => {
  const layers = new Set<number>();
  for (const key of keys) {
    for (const { loopId, chunkIndex } of model.chunksByEntityKey.get(key) ??
      []) {
      const chunk = model.loopsById.get(loopId)?.chunks[chunkIndex];
      if (chunk) {
        layers.add(chunk.layer);
      }
    }
  }
  return layers;
};

/** The layers a selection draws on. */
export const selectionLayers = (
  selection: Selection,
  model: DrawingModel,
): ReadonlySet<number> => {
  const layers = new Set<number>();
  selection.forEach((ref) => {
    const loop = model.loopsById.get(ref.loopId);
    if (!loop) {
      return;
    }
    const chunks =
      ref.kind === 'edge' ? [loop.chunks[ref.chunkIndex]] : loop.chunks;
    chunks.forEach((chunk) => {
      if (chunk) {
        layers.add(chunk.layer);
      }
    });
  });
  return layers;
};

/**
 * Entities whose roles differ between two mappings.
 *
 * Used to work out what an undo or redo actually moved, so the layers holding it
 * can be revealed. Compared element-wise rather than as sets: role order is
 * canonicalised on write, so two equal assignments are equal in sequence too.
 */
export const changedEntityKeys = (
  before: ReadonlyMap<DxfEntityKey, readonly DxfMappingRole[]> | null,
  after: ReadonlyMap<DxfEntityKey, readonly DxfMappingRole[]> | null,
): ReadonlySet<DxfEntityKey> => {
  const changed = new Set<DxfEntityKey>();
  if (before === after) {
    return changed;
  }

  const isSame = (
    a: readonly DxfMappingRole[] | undefined,
    b: readonly DxfMappingRole[] | undefined,
  ) =>
    a === b ||
    (a !== undefined &&
      b !== undefined &&
      a.length === b.length &&
      a.every((role, index) => role === b[index]));

  before?.forEach((roles, key) => {
    if (!isSame(roles, after?.get(key))) {
      changed.add(key);
    }
  });
  after?.forEach((roles, key) => {
    if (!isSame(before?.get(key), roles)) {
      changed.add(key);
    }
  });

  return changed;
};

/** True when two assignment maps carry the same roles on every entity. */
export const assignmentsEqual = (
  a: ReadonlyMap<DxfEntityKey, readonly DxfMappingRole[]> | null,
  b: ReadonlyMap<DxfEntityKey, readonly DxfMappingRole[]> | null,
): boolean => changedEntityKeys(a, b).size === 0;

/**
 * Entity keys that seed to `ignore` rather than to `cutting`.
 *
 * Three sources, for two different reasons. PMI and text are the AC's "GD&T and
 * callouts" — real annotation a person drew, which interrogation should skip.
 * Unstitched keys (fills, single-point POINTs) are here because they can never be
 * selected or painted, so leaving them on `cutting` would put geometry into the
 * cut role that no one can ever see or take back out.
 */
export const defaultIgnoredEntityKeys = (
  model: DrawingModel,
): ReadonlySet<DxfEntityKey> =>
  new Set([
    ...model.pmiEntityKeys,
    ...model.textEntityKeys,
    ...model.unstitchedEntityKeys,
  ]);

/**
 * The mapping a drawing starts with when a person enters mapping mode on a file
 * that has none.
 *
 * Everything is cut except annotation, per the AC, and *everything* is present —
 * there is no such thing as a partially mapped file, so this covers every key in
 * the model rather than only the ones that can be seen or picked.
 */
export const seedAssignments = (
  model: DrawingModel,
): ReadonlyMap<DxfEntityKey, readonly DxfMappingRole[]> => {
  const ignored = defaultIgnoredEntityKeys(model);
  const seeded = new Map<DxfEntityKey, readonly DxfMappingRole[]>();
  const CUT = [DXF_MAPPING_ROLE.CUTTING] as const;
  const IGNORE = [DXF_MAPPING_ROLE.IGNORE] as const;

  model.entityKeys.forEach((key) => {
    seeded.set(key, ignored.has(key) ? IGNORE : CUT);
  });

  return seeded;
};
